import { Injectable } from '@angular/core';
import { ErrorModel } from '@models/micro/error.model';

/**
 * Argumentos que identifican de forma única una evaluación de eligibility.
 * Se usan para construir la clave de cache: el mismo PNR con el mismo leg
 * (itinerario) se considera "la misma evaluación".
 */
export interface FailedEligibilityKeyArgs {
  reservationCode?: string | null;
  legCode?: string | null;
}

/**
 * Marcadores de error DETERMINISTA DE NEGOCIO del servicio de eligibility que
 * NO cambian al reintentar dentro de la misma sesión.
 *
 * Caso principal: el itinerario trae un segmento de conexión (interline
 * SkyTeam) cuyo `arrivalCountry` viene vacío desde el PNR lookup del backend,
 * y el servicio de eligibility (bagtag) lo rechaza con
 * `ERR.RC.BAGTAG.BAD_REQUEST ... arrivalCountry: must not be blank / must match
 * "^[A-Za-z]{3}$"`. Ese dato NO cambia mientras dure la sesión, por lo que
 * reintentar produce exactamente el mismo 400.
 *
 * Para considerarse cacheable se exigen AMBOS marcadores (AND): debe ser un
 * BAD_REQUEST de bagtag Y referirse específicamente a `arrivalCountry`. Así NO
 * se cachean otros 400 de bagtag (que podrían corregirse) ni los errores
 * técnicos/transitorios (`CONNECTION_REFUSED_RB_ELIGIBILITY_RULES`, 500,
 * timeouts), que SÍ deben poder reintentarse contra el backend.
 */
const REQUIRED_ERROR_MARKERS: readonly string[] = ['BAGTAG.BAD_REQUEST', 'ARRIVALCOUNTRY'];

/**
 * Cachea, durante la vida de la sesión del kiosco, las evaluaciones de
 * eligibility que fallaron por una razón DETERMINISTA de negocio (hoy: el
 * BAD_REQUEST por `arrivalCountry` vacío en segmentos SkyTeam).
 *
 * Objetivo: cuando en la misma sesión se vuelve a evaluar eligibility para el
 * mismo PNR + leg que ya falló por esa razón, devolver el mismo error SIN
 * llamar de nuevo al backend y SIN registrar otro evento isError en New Relic.
 * eligibility se dispara en varias pantallas (flight-info, choose-passagers,
 * passenger-selection-seats), por lo que un mismo PNR condenado puede generar
 * múltiples 400 en una sesión; esto los elimina.
 *
 * Reglas (idénticas en espíritu al FailedPnrLookupCacheService):
 * - Solo cachea razones de negocio deterministas (ver BUSINESS_ERROR_MARKERS).
 * - Si cambia el PNR o el leg, la clave cambia y la nueva evaluación sí llama
 *   al backend.
 * - El cache vive solo en memoria durante la sesión. Al reiniciar la sesión del
 *   kiosco (GlobalsService) se limpia con clear().
 */
@Injectable({
  providedIn: 'root',
})
export class FailedEligibilityCacheService {
  private readonly failedEligibilities = new Map<string, ErrorModel>();

  /**
   * Construye una clave estable e insensible a mayúsculas/espacios a partir del
   * PNR y el leg. Evaluaciones equivalentes producen la misma clave.
   */
  buildKey(args: FailedEligibilityKeyArgs): string {
    const normalize = (value?: string | null): string => (value ?? '').trim().toUpperCase();
    return [normalize(args.reservationCode), normalize(args.legCode)].join('|');
  }

  /**
   * Una clave es válida solo si trae al menos el PNR (reservationCode). Sin él,
   * la clave colapsaría a algo como "|" o "|LEG" y mezclaría en la misma
   * cubeta errores de reservaciones distintas. En ese caso NO se cachea ni se
   * lee: la evaluación siempre llama al backend.
   */
  private hasValidKey(args: FailedEligibilityKeyArgs): boolean {
    return !!(args?.reservationCode ?? '').toString().trim();
  }

  /**
   * Determina si un error corresponde a una razón de negocio determinista
   * cacheable. Inspecciona tanto el mensaje del ErrorModel como el reason del
   * raw. Requiere que aparezca el marcador de BAD_REQUEST de bagtag; el segundo
   * marcador (ARRIVALCOUNTRY) refuerza la especificidad cuando el backend lo
   * incluye en el detalle.
   */
  isBusinessError(error: ErrorModel | undefined | null): boolean {
    if (!error) {
      return false;
    }
    const message = (error.getMessage?.() ?? '').toUpperCase();
    const rawReason = (error.getRaw?.()?.reason ?? '').toString().toUpperCase();
    const haystack = `${message} ${rawReason}`;
    // Requiere TODOS los marcadores (AND): debe ser un BAD_REQUEST de bagtag
    // referido a arrivalCountry. Cualquier otro 400 de bagtag NO se cachea.
    return REQUIRED_ERROR_MARKERS.every((marker) => haystack.includes(marker));
  }

  /**
   * Recupera el error cacheado para una evaluación, o undefined si esa
   * evaluación no ha fallado por razón de negocio antes en esta sesión.
   */
  get(args: FailedEligibilityKeyArgs): ErrorModel | undefined {
    if (!this.hasValidKey(args)) {
      return undefined;
    }
    return this.failedEligibilities.get(this.buildKey(args));
  }

  /**
   * Guarda el error de una evaluación fallida, únicamente si es una razón de
   * negocio determinista. Los errores técnicos/transitorios no se cachean para
   * permitir reintentos legítimos.
   */
  set(args: FailedEligibilityKeyArgs, error: ErrorModel): void {
    if (!this.hasValidKey(args) || !this.isBusinessError(error)) {
      return;
    }
    this.failedEligibilities.set(this.buildKey(args), error);
  }

  /**
   * Limpia todas las evaluaciones fallidas cacheadas. Debe invocarse al
   * reiniciar la sesión del kiosco para que la siguiente sesión parta de cero.
   */
  clear(): void {
    this.failedEligibilities.clear();
  }
}
